import React, { useMemo } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   SPECIFICATION REVIEW PILOT PANEL
   ----------------------------------------------------------------------------
   VERSION STANDARDISÉE
   - grammaire strictement alignée sur le template panneau
   - pas de lignes de séparation
   - valeurs alignées à droite via KeyValueRow
   - bloc Readiness supprimé du contenu
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",
  border: theme?.colors?.borders?.default || "#d6dee4",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  caneva: theme?.colors?.text?.caneva || "#c24c2c",
  green: theme?.colors?.text?.green || "#1A8C74",
  orange: theme?.colors?.text?.secondary || "#B8621B",
};

/* ============================================================================
   STYLES STANDARDISÉS
============================================================================ */

const S = {
  panel: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 22,
    boxSizing: "border-box",
  },

  panelHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minHeight: 12,
  },

  panelTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "1.02rem",
    lineHeight: 1.1,
    color: T.text,
    fontWeight: 600,
  },

  block: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    paddingBottom: 2,
  },

  blockTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.84rem",
    lineHeight: 1.12,
    color: theme?.colors?.text?.caneva || "#c24c2c",
    fontWeight: 600,
  },

  rows: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  kvRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: 12,
  },

  kvLabel: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.74rem",
    lineHeight: 1.2,
    color: T.soft,
    minWidth: 0,
  },

  kvValue: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.76rem",
    lineHeight: 1.2,
    fontWeight: 600,
    textAlign: "right",
    whiteSpace: "nowrap",
  },

  listBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
    alignItems: "flex-end",
  },

  listItem: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.73rem",
    lineHeight: 1.42,
    color: T.text,
    textAlign: "right",
  },
};

/* ============================================================================
   DONNÉES MOCK
============================================================================ */

const DEFAULT_PILOT_DATA = {
  reviewItems: [
    {
      id: "sp-001",
      maturityStatus: "Validée",
      technicalStatus: "Maîtrisée",
      projectImpact: "Sous contrôle",
      coverageStatus: "Couverte",
      interfaceResolved: true,
      issueType: "Cohérence",
    },
    {
      id: "sp-002",
      maturityStatus: "À consolider",
      technicalStatus: "Instable",
      projectImpact: "Critique",
      coverageStatus: "Couverte",
      interfaceResolved: false,
      issueType: "Interface",
    },
    {
      id: "sp-003",
      maturityStatus: "En cours",
      technicalStatus: "Instable",
      projectImpact: "Critique",
      coverageStatus: "Partielle",
      interfaceResolved: true,
      issueType: "Couverture",
    },
    {
      id: "sp-004",
      maturityStatus: "À consolider",
      technicalStatus: "Partielle",
      projectImpact: "Sous contrôle",
      coverageStatus: "Couverte",
      interfaceResolved: true,
      issueType: "Cohérence",
    },
    {
      id: "sp-005",
      maturityStatus: "Validée",
      technicalStatus: "Maîtrisée",
      projectImpact: "Neutre",
      coverageStatus: "Couverte",
      interfaceResolved: true,
      issueType: "Usage",
    },
    {
      id: "sp-006",
      maturityStatus: "En cours",
      technicalStatus: "Partielle",
      projectImpact: "Critique",
      coverageStatus: "Partielle",
      interfaceResolved: false,
      issueType: "Coordination",
    },
    {
      id: "sp-007",
      maturityStatus: "Validée",
      technicalStatus: "Maîtrisée",
      projectImpact: "Sous contrôle",
      coverageStatus: "Couverte",
      interfaceResolved: true,
      issueType: "Sécurité",
    },
    {
      id: "sp-008",
      maturityStatus: "À consolider",
      technicalStatus: "Instable",
      projectImpact: "Bloquant",
      coverageStatus: "Partielle",
      interfaceResolved: false,
      issueType: "Décision",
    },
    {
      id: "sp-009",
      maturityStatus: "Non traitée",
      technicalStatus: "Non définie",
      projectImpact: "Critique",
      coverageStatus: "Non couverte",
      interfaceResolved: true,
      issueType: "Couverture",
    },
  ],

  
};

/* ============================================================================
   HELPERS
============================================================================ */

const safePct = (value, total) => {
  if (!total) return 0;
  return Math.round((value / total) * 100);
};

const computeMetrics = (pilotData) => {
  const items = pilotData?.reviewItems || [];
  const total = items.length;

  const maturity = {
    nonTraitee: items.filter((i) => i.maturityStatus === "Non traitée").length,
    enCours: items.filter((i) => i.maturityStatus === "En cours").length,
    aConsolider: items.filter((i) => i.maturityStatus === "À consolider").length,
    validee: items.filter((i) => i.maturityStatus === "Validée").length,
  };

  const technical = {
    nonDefinie: items.filter((i) => i.technicalStatus === "Non définie").length,
    instable: items.filter((i) => i.technicalStatus === "Instable").length,
    partielle: items.filter((i) => i.technicalStatus === "Partielle").length,
    maitrisee: items.filter((i) => i.technicalStatus === "Maîtrisée").length,
  };

  const impact = {
    bloquant: items.filter((i) => i.projectImpact === "Bloquant").length,
    critique: items.filter((i) => i.projectImpact === "Critique").length,
    sousControle: items.filter((i) => i.projectImpact === "Sous contrôle").length,
    neutre: items.filter((i) => i.projectImpact === "Neutre").length,
  };

  const coverage = {
    couverte: items.filter((i) => i.coverageStatus === "Couverte").length,
    partielle: items.filter((i) => i.coverageStatus === "Partielle").length,
    nonCouverte: items.filter((i) => i.coverageStatus === "Non couverte").length,
  };

  const unresolvedInterfaces = items.filter((i) => !i.interfaceResolved).length;

  const remaining = {
    aTraiter:
      maturity.nonTraitee + maturity.enCours + maturity.aConsolider,
    interfaces: unresolvedInterfaces,
    couvertureFaible: coverage.partielle + coverage.nonCouverte,
    verrous: impact.bloquant + impact.critique,
  };

  const reviewRate = safePct(
    maturity.enCours + maturity.aConsolider + maturity.validee,
    total
  );
  const validationRate = safePct(maturity.validee, total);
  const masteryRate = safePct(technical.maitrisee, total);
  const coverageRate = safePct(coverage.couverte + coverage.partielle, total);

  const impactProjection = {
    cost: items.filter(
      (i) => i.projectImpact === "Bloquant" || i.projectImpact === "Critique"
    ).length,
    timeline: items.filter(
      (i) =>
        i.projectImpact === "Bloquant" ||
        i.issueType === "Coordination" ||
        i.issueType === "Interface"
    ).length,
    deliverables: items.filter(
      (i) =>
        i.maturityStatus === "Non traitée" ||
        i.maturityStatus === "À consolider"
    ).length,
  };

  return {
    total,
    maturity,
    technical,
    impact,
    coverage,
    remaining,
    unresolvedInterfaces,
    reviewRate,
    validationRate,
    masteryRate,
    coverageRate,
    impactProjection,
    actionFocus: (pilotData?.actionFocus || []).slice(0, 6),
  };
};

const getValueColor = (type, value) => {
  if (type === "bloquant" && value > 0) return T.redText;
  if (type === "critique" && value > 0) return T.orangeText;
  if (type === "warning" && value > 0) return T.orangeText;
  if (type === "positive" && value > 0) return T.greenText;
  return T.text;
};

/* ============================================================================
   UI
============================================================================ */

const PanelBlock = ({ title, children }) => (
  <div style={S.block}>
    <div style={S.blockTitle}>{title}</div>
    {children}
  </div>
);

const KeyValueRow = ({ label, value, valueColor = T.text, valueStyle = {} }) => (
  <div style={S.kvRow}>
    <div style={S.kvLabel}>{label}</div>
    <div style={{ ...S.kvValue, color: valueColor, ...valueStyle }}>{value}</div>
  </div>
);

/* ============================================================================
   COMPONENT
============================================================================ */

const SpecificationReviewPilotPanel = ({
  pilotData = DEFAULT_PILOT_DATA,
}) => {
  const metrics = useMemo(() => computeMetrics(pilotData), [pilotData]);

  return (
    <div style={S.panel}>
      <div style={S.panelHeader}>
        <div style={S.panelTitle}>Review Pilot</div>
      </div>

      <PanelBlock title="État global">
        <div style={S.rows}>
          <KeyValueRow label="Travail engagé" value={`${metrics.reviewRate}%`} />
          <KeyValueRow label="Spécifications validées" value={`${metrics.validationRate}%`} />
          <KeyValueRow label="Niveau de maîtrise" value={`${metrics.masteryRate}%`} />
        </div>
      </PanelBlock>

      <PanelBlock title="Couverture">
        <div style={S.rows}>
          <KeyValueRow label="Couverture suffisante" value={`${metrics.coverageRate}%`} />
          <KeyValueRow
            label="Éléments partiellement couverts"
            value={metrics.coverage.partielle}
            valueColor={getValueColor("warning", metrics.coverage.partielle)}
          />
          <KeyValueRow
            label="Éléments non couverts"
            value={metrics.coverage.nonCouverte}
            valueColor={getValueColor("bloquant", metrics.coverage.nonCouverte)}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Verrous et cohérence">
        <div style={S.rows}>
          <KeyValueRow
            label="Sujets bloquants"
            value={metrics.impact.bloquant}
            valueColor={getValueColor("bloquant", metrics.impact.bloquant)}
          />
          <KeyValueRow
            label="Sujets critiques"
            value={metrics.impact.critique}
            valueColor={getValueColor("critique", metrics.impact.critique)}
          />
          <KeyValueRow
            label="Interfaces non stabilisées"
            value={metrics.unresolvedInterfaces}
            valueColor={getValueColor("warning", metrics.unresolvedInterfaces)}
          />
          <KeyValueRow
            label="Zones instables"
            value={metrics.technical.instable}
            valueColor={getValueColor("warning", metrics.technical.instable)}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Charge restante">
        <div style={S.rows}>
          <KeyValueRow
            label="Sujets encore à traiter"
            value={metrics.remaining.aTraiter}
            valueColor={metrics.remaining.aTraiter > 0 ? T.text : T.greenText}
          />
          <KeyValueRow
            label="Couverture faible"
            value={metrics.remaining.couvertureFaible}
            valueColor={getValueColor("warning", metrics.remaining.couvertureFaible)}
          />
          <KeyValueRow
            label="Décisions / verrous"
            value={metrics.remaining.verrous}
            valueColor={getValueColor("bloquant", metrics.remaining.verrous)}
          />
        </div>

      </PanelBlock>

      <PanelBlock title="Impacts projet">
        <div style={S.rows}>
          <KeyValueRow
            label="Impact estimation"
            value={metrics.impactProjection.cost}
            valueColor={getValueColor("warning", metrics.impactProjection.cost)}
          />
          <KeyValueRow
            label="Impact planning"
            value={metrics.impactProjection.timeline}
            valueColor={getValueColor("warning", metrics.impactProjection.timeline)}
          />
          <KeyValueRow
            label="Impact livrables"
            value={metrics.impactProjection.deliverables}
            valueColor={getValueColor("warning", metrics.impactProjection.deliverables)}
          />
        </div>
      </PanelBlock>
    </div>
  );
};

export default SpecificationReviewPilotPanel;